import React from 'react';
import { CheckCircle2, Zap, Pencil, AlertCircle, Trash2 } from 'lucide-react';

/**
 * Visual topology of one distribution board, derived from its
 * `numberingScheme` + the cards belonging to it. Renders every way
 * (pending, done, spare) so the engineer can spot mis-labelled ways
 * at a glance.
 *
 * Layout rules:
 *  - three_phase_by_row (3 phases): rows × 3 columns (L1 / L2 / L3)
 *  - single_phase_ltr: one row, ways left-to-right
 *  - single_phase_rtl: one row, ways right-to-left (visually flipped)
 *  - unknown: linear left-to-right with a small ⓘ note
 *
 * Click a cell to jump that card into the active editor.
 */

const STATUS_TONE = {
  pending: 'border-slate-300 bg-white',
  done: 'border-emerald-300 bg-emerald-50',
  skipped: 'border-slate-200 bg-slate-100 opacity-60',
};

const Cell = ({ card, position, label, onClick, scheme }) => {
  const isSpare = Boolean(card?.isSpare || card?.final?.isSpare);
  const isGangSibling = Boolean(card?.final?.isGangSibling);
  const gangParent = card?.final?.gangParentCircuitNumber || '';
  const status = card?.status || 'pending';
  const isDone = status === 'done';
  const guessDesc = card?.aiGuess?.description || card?.final?.description || '';
  const finalDesc = card?.final?.description || '';
  const finalRating = card?.final?.mcbRating || card?.aiGuess?.mcbRating || '';
  const tone = isGangSibling
    ? 'border-indigo-300 bg-indigo-50'
    : isSpare
      ? 'border-amber-300 bg-amber-50'
      : STATUS_TONE[status] || STATUS_TONE.pending;
  const isPending = status === 'pending';

  return (
    <button
      type="button"
      onClick={card ? () => onClick?.(card.id) : undefined}
      disabled={!card}
      className={`relative text-left rounded-md border p-2.5 min-h-[78px] transition-all ${tone} ${
        card ? 'hover:shadow-sm hover:-translate-y-px cursor-pointer' : 'cursor-default'
      } ${isPending ? 'border-dashed' : ''}`}
      data-testid={`board-cell-${position}`}
      title={card ? `${label} · ${isGangSibling ? `Part of ${gangParent} 3-pole gang` : isSpare ? 'Spare' : isDone ? 'Labelled' : 'Pending'}` : `${label} · empty`}
    >
      <div className="flex items-start justify-between gap-1">
        <span className="font-mono text-[11px] font-semibold text-slate-700 tabular-nums">
          {label}
        </span>
        {isGangSibling ? (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-indigo-700 uppercase tracking-wide">
            ↑ {gangParent}
          </span>
        ) : isSpare ? (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-700 uppercase tracking-wide">
            <Zap className="h-2.5 w-2.5" /> Spare
          </span>
        ) : isDone ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
        ) : isPending ? (
          <Pencil className="h-3 w-3 text-slate-400" />
        ) : null}
      </div>
      {isGangSibling ? (
        <div className="mt-1 text-[11px] text-indigo-700 italic leading-tight">
          part of {gangParent} 3-pole gang
        </div>
      ) : !isSpare && (
        <>
          <div className="mt-1 text-[11px] text-slate-700 line-clamp-2 leading-tight">
            {finalDesc || guessDesc || (
              <span className="text-slate-400 italic">no description</span>
            )}
          </div>
          {finalRating && (
            <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{finalRating}</div>
          )}
        </>
      )}
      {!card && (
        <div className="text-[10px] text-slate-400 mt-1">no card</div>
      )}
    </button>
  );
};

const BoardGrid = ({ board, cards = [], onCardClick, onDelete }) => {
  if (!board) return null;
  const scheme = board.numberingScheme || 'unknown';
  const phases = board.phases || 1;
  const wayCount = Math.max(board.wayCount || 0, 0);
  // `wayCount` is the number of WAYS (rows) — for 3-phase boards
  // the total number of grid CELLS is `ways × phases` (e.g. an 8-way
  // 3-phase board has 24 circuits: 1L1, 1L2, 1L3, 2L1, … 8L3).
  // Earlier this conflated ways with cells, which capped 8-way
  // boards at 8 cells (1L1…3L2) instead of 24.
  const totalPositions =
    scheme === 'three_phase_by_row' && phases === 3
      ? wayCount * 3
      : wayCount;
  // Always grow the grid to cover every card we received, in case
  // the board's stored `wayCount` is stale or the AI created extra
  // cards (e.g. after editing the layout).
  const positions = Math.max(totalPositions, cards.length);

  // Index cards by position for O(1) lookup
  const byPos = new Map();
  for (const c of cards) {
    if (typeof c.position === 'number') byPos.set(c.position, c);
  }

  // Compute label per position based on scheme (mirrors backend
  // _compute_circuit_number; lets us label empty-position cells too)
  const labelFor = (position) => {
    if (scheme === 'three_phase_by_row' && phases === 3) {
      const row = Math.floor(position / 3) + 1;
      const phase = (position % 3) + 1;
      return `${row}L${phase}`;
    }
    return String(position + 1);
  };

  // Build rows
  let rows = [];
  if (scheme === 'three_phase_by_row' && phases === 3) {
    const rowCount = Math.ceil(positions / 3);
    for (let r = 0; r < rowCount; r++) {
      const cells = [];
      for (let p = 0; p < 3; p++) {
        const pos = r * 3 + p;
        if (pos >= positions) break;
        cells.push({ position: pos, label: labelFor(pos) });
      }
      rows.push(cells);
    }
  } else {
    // Single-phase: one row of `positions` cells. RTL handled via CSS flip.
    rows = [
      Array.from({ length: positions }, (_, p) => ({ position: p, label: labelFor(p) })),
    ];
  }

  const isRtl = scheme === 'single_phase_rtl';

  // Stats footer. Gang siblings (1L2/1L3 auto-completed when 1L1 was
  // ticked as 3-pole) count as labelled — they ARE labelled work, just
  // collapsed under the parent's circuit number. We surface their
  // count separately so the engineer can see how many collapses
  // happened on the board ("3 labelled · 2 collapsed · 5 ways covered").
  const gangSiblingCount = cards.filter(c => c.final?.isGangSibling).length;
  const labelled = cards.filter(c => c.status === 'done' && !c.isSpare && !c.final?.isSpare).length;
  const spares = cards.filter(c => c.isSpare || c.final?.isSpare).length;
  const pending = cards.filter(c => c.status === 'pending').length;
  const progress = positions > 0 ? Math.round(((labelled + spares) / positions) * 100) : 0;

  const SchemeBadge = (
    <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">
      {scheme === 'three_phase_by_row' && '3-phase · row order'}
      {scheme === 'single_phase_ltr' && 'Single-phase · L → R'}
      {scheme === 'single_phase_rtl' && 'Single-phase · R → L'}
      {(scheme === 'unknown' || !scheme) && 'Layout unknown'}
    </span>
  );

  return (
    <section
      className="bg-white border border-slate-200 rounded-md overflow-hidden"
      data-testid={`board-grid-${board.id}`}
    >
      <div className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h3 className="font-display uppercase tracking-wide text-slate-900 text-sm">Board topology</h3>
          {SchemeBadge}
          <span className="text-[11px] text-slate-500">· {wayCount} ways</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-[11px] text-slate-500 tabular-nums">
            {labelled} labelled · {spares} spare · {pending} pending{gangSiblingCount > 0 && (
              <> · <span className="text-indigo-700" title={`${gangSiblingCount} ways auto-completed via 3-pole gang collapse`}>{gangSiblingCount} via gang</span></>
            )} · <span className="font-semibold text-slate-700">{progress}%</span>
          </div>
          {onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(board); }}
              className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
              title="Delete this board topology and all its training cards"
              aria-label="Delete board"
              data-testid={`delete-board-grid-${board.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {scheme === 'unknown' && (
        <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-800 flex items-center gap-2">
          <AlertCircle className="h-3.5 w-3.5" />
          AI couldn&apos;t determine the numbering scheme — pick one in the layout banner above so the cells line up correctly.
        </div>
      )}

      <div className="p-4 space-y-2">
        {rows.map((row, rIdx) => (
          <div
            key={rIdx}
            className={`grid gap-2 ${isRtl ? 'direction-rtl' : ''}`}
            style={{
              gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
              direction: isRtl ? 'rtl' : 'ltr',
            }}
          >
            {row.map(({ position, label }) => (
              <Cell
                key={position}
                position={position}
                label={label}
                card={byPos.get(position)}
                onClick={onCardClick}
                scheme={scheme}
              />
            ))}
          </div>
        ))}
        {wayCount === 0 && (
          <div className="text-center text-sm text-slate-500 py-8">
            No ways extracted from this photo yet.
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/60 flex items-center gap-4 text-[11px] text-slate-600 flex-wrap">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded border border-slate-300 border-dashed bg-white" />
          Pending
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded border border-emerald-300 bg-emerald-50" />
          Labelled
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded border border-amber-300 bg-amber-50" />
          Spare
        </span>
        <span className="text-slate-400 ml-auto">Click a cell to edit that way.</span>
      </div>
    </section>
  );
};

export default BoardGrid;
